import { useTranslation } from 'react-i18next'
import { ASSIGNABLE_ROLES, type Role } from '@/auth/roles'
import { ROLE_HINT_KEYS, ROLE_LABEL_KEYS } from '@/components/users/roleLabels'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

/**
 * Exactly one role per account. The backend applies `ADMIN > SALES`, so an administrator needs no
 * second assignment — offering checkboxes would invite combinations that mean nothing.
 */
export function RoleSelect({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string
  value: Role
  onChange: (role: Role) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()

  return (
    <Select value={value} onValueChange={(next) => onChange(next as Role)} disabled={disabled}>
      <SelectTrigger id={id} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ASSIGNABLE_ROLES.map((role) => (
          // `textValue` is not optional when the children are more than a string: without it Radix
          // has nothing to put in the trigger and renders the placeholder as though no role were
          // selected. Same bug as the one fixed in TradeFairSelect.
          <SelectItem key={role} value={role} textValue={t(ROLE_LABEL_KEYS[role])}>
            <span className="flex flex-col items-start gap-0.5">
              <span>{t(ROLE_LABEL_KEYS[role])}</span>
              <span className="text-muted-foreground text-xs">{t(ROLE_HINT_KEYS[role])}</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
